// profilo-api.js — amm-v3 (A6, ondata 33): parte PURA della vista "Profilo
// azienda" (pagina 'profilo' — l'HUB con la scheda anagrafica azienda + le
// caratteristiche fiscali + le sotto-sezioni Tipologie/Parametri/Proroghe e i
// pannelli DELEGATI Promemoria/Registro/Spazio/Cestino). Testabile sotto node
// (contract test server/src/lib/amm-v3-profilo.test.js).
//
// PRINCIPI (rito A1..A5/proc-v3, spike o27 §2.2/§2.3):
//  - SEAM = renderProfilo (viewId 'profilo'): l'INTERA pagina è UNA vista React
//    (wrap di AMM.renderPage, motore A1). NON esiste un dispatch per-sotto-
//    sezione (le sotto-sezioni sono mount interni raggiungibili SOLO dalla
//    pagina): quindi FLAG UNICA shell_amm_v3_profilo per tutto l'hub (nessun
//    beneficio di rollback da flag per-sotto-sezione: al seam renderPage la
//    pagina è tutta v1 o tutta v3).
//  - MIGRATE NATIVE (React): scheda azienda + 8 flag fiscali + anteprima
//    impatto (dry-run) + salva-e-rigenera; e le 3 sotto-sezioni TABELLARI ad
//    alto valore — Tipologie, Parametri normativi, Proroghe (liste + "+ Nuovo"
//    + elimina; gli EDITOR modali restano v1: editTipologia/editParametro/
//    editProroga DELEGATI, payload IDENTICO per costruzione).
//  - DELEGATE al v1 (mount in container React, confine dichiarato per A7):
//    Promemoria email (renderPromemoriaSection), Registro modifiche
//    (renderRegistroModifiche), Spazio allegati (renderSpazioAllegati),
//    Cestino (renderCestino) — pannelli stateful/paginati/di consultazione.
//  - TESTI VERBATIM dal v1 (renderProfilo r.3016-3168 + sotto-sezioni): il
//    contract test pinna OGNI stringa di PA_TEXTS sul sorgente v1.
//  - FORMULE del v1 replicate come funzioni pure (payload salva, pluralizzazioni
//    anteprima, validità parametro, titolo regola proroga) — stessi quirk.
//  - admin-module.js resta OFF-LIMITS: zero byte cambiati lì.
//  - MAI 'nav:badge' o canali nuovi da client/src/amm-v3/**.

import { apiPathFn } from './api.js';

// ---- metodi di window.AMM che il v3 DELEGA (guardie: senza uno solo di
// questi la vista sarebbe monca ⇒ install inerte, mondo v1 intatto) ----
//
// ONDATA 37 (A7 completamento): i 4 pannelli Promemoria/Registro/Spazio/Cestino
// sono ora NATIVI React (non più delegati ai renderX del v1). Perciò:
//  - renderPromemoriaSection/renderRegistroModifiche/renderSpazioAllegati/
//    renderCestino NON sono più dipendenze (rimossi dalle guardie);
//  - upsertParametroVigente ENTRA come dipendenza: il salva del Promemoria
//    DELEGA la SCRITTURA alla primitiva v1 (stesso "update in place della riga
//    vigente", zero logica di scrittura reimplementata — mandato ondata 37).
export const PR_DELEGATED_METHODS = [
  'renderPage',              // il seam stesso (wrap) + refresh post-azione
  'renderProfilo',           // la vista v1 wrappata (drift guard: rename ⇒ rosso)
  'isAdmin',                 // "Salva e rigenera"/elimina/Ripristina solo admin (parità v1)
  'toast',                   // esiti (stesso canale del v1)
  'delSafe',                 // eliminazione con alert d'errore (primitiva v1)
  'editTipologia',           // modale tipologia — resta v1 (payload identico)
  'editParametro',           // modale parametro — resta v1
  'editProroga',             // modale proroga — resta v1
  'upsertParametroVigente',  // Promemoria salva — SCRITTURA delegata al v1 (A7)
];

// ---- funzioni di window.api.amm usate DIRETTAMENTE dal v3 (letture + azioni;
// create/update della scheda vivono nei modali/primitive v1 delegati) ----
export const PR_API_PATHS = [
  'profilo.get',             // GET /profilo (scheda azienda + flag)
  'profilo.update',          // PATCH /profilo (salva e rigenera)
  'profilo.anteprimaImpatto',// POST /profilo/anteprima-impatto (dry-run)
  'tipologie.list',          // GET /tipologie
  'parametri.list',          // GET /parametri (scheda + Promemoria: valori vigenti)
  'parametri.remove',        // DELETE /parametri/:id
  'proroghe.list',           // GET /proroghe
  'proroghe.remove',         // DELETE /proroghe/:id
  'regole.list',             // GET /regole (per titolo regola + select proroga)
  // A7 (ondata 37) — pannelli nativi: stesse letture/azioni del v1
  'promemoria.stato',        // GET /promemoria/stato (toggle + email + log)
  'promemoria.test',         // POST /promemoria/test (invio di prova)
  'storiaRegistro',          // GET /storia (Registro modifiche, filtri+paginazione)
  'attachments.spazioOccupato', // GET /attachments/spazio-occupato (Spazio allegati)
  'cestino.list',            // GET /cestino (elenco cestinati)
  'cestino.ripristina',      // POST /cestino/:entita/:id/ripristina (NON distruttivo)
];

// icone unicode (parità con le entity del v1: &#9998; ✎, &#128465; 🗑, ⚠) —
// codepoint espliciti per evitare ambiguità di encoding nel .jsx
export const IC_EDIT = '✎';       // ✎  (v1 &#9998;)
export const IC_DEL = '\u{1F5D1}';     // 🗑  (v1 &#128465;)
export const IC_WARN = '⚠';       // ⚠  (v1, note "da confermare")
export const ARROW = '→';         // →  (v1 &rarr;)

// ---- TESTI VERBATIM dal v1 (pinnati dal contract test sul sorgente) ----
export const PA_TEXTS = {
  // header + scheda azienda
  title: 'Profilo azienda',
  subtitle: 'Decide quali scadenze ti riguardano',
  anteprima: 'Anteprima impatto',
  salvaRigenera: 'Salva e rigenera',
  lRagioneSociale: 'Ragione sociale',
  lPartitaIva: 'Partita IVA',
  lCodiceFiscale: 'Codice fiscale',
  lCodiceAteco: 'Codice ATECO',
  lFormaGiuridica: 'Forma giuridica',
  lRegimeIva: 'Regime IVA',
  lMeseChiusura: 'Mese chiusura esercizio',
  lGiornoChiusura: 'Giorno chiusura esercizio',
  lPatPrincipale: 'PAT INAIL principale <span class="text-slate-400 font-normal">(PAT diverse per edile e ufficio)</span>',
  caratteristicheFiscali: 'Caratteristiche fiscali',
  // sotto-sezioni (header)
  tipologie: 'Tipologie del resoconto',
  nuovaTipologia: '+ Nuova tipologia',
  parametri: 'Parametri normativi',
  nuovoParametro: '+ Nuovo parametro',
  parametriHint: 'I calcoli useranno il valore vigente alla data di riferimento.',
  proroghe: 'Proroghe',
  nuovaProroga: '+ Nuova proroga',
  promemoria: 'Promemoria email',
  registroModifiche: 'Registro modifiche',
  spazioAllegati: 'Spazio allegati',
  cestino: 'Cestino',
  // tabelle
  emptyTipologie: 'Nessuna tipologia.',
  emptyParametri: 'Nessun parametro configurato.',
  emptyProroghe: 'Nessuna proroga registrata.',
  modifica: 'modifica',
  ordinePrefix: 'ordine ',
  tutte: 'tutte',
  dash: '—',
  caricamento: 'Caricamento…',
  // anteprima (dry-run) — VERBATIM
  anteprimaInvariato: 'Con questi flag lo scadenzario resterebbe invariato: nessuna nuova scadenza, nessuna che smette di essere dovuta.',
  seSalvi: 'Se salvi con questi flag:',
  nessunaNuova: 'nessuna nuova scadenza',
  anteprimaNota: 'Anteprima: nessun dato è stato salvato. Premi "Salva e rigenera" per applicare davvero.',
  // frammenti pluralizzazione anteprima (fragment VERBATIM presenti nel v1)
  fragTenereOcchio: " da tenere d'occhio",
  fragCheSmette: ' che smette',
  fragEssereDovut: ' di essere dovut',
  // esiti scrittura
  errorePrefix: 'Errore: ',
  // conferme/toast elimina
  toastParametroEliminato: 'Parametro eliminato',
  toastProrogaEliminata: 'Proroga eliminata · scadenzario rigenerato',
  confirmProroga: 'Eliminare questa proroga?',

  // ===== A7 (ondata 37) — TESTI VERBATIM dei 4 pannelli NATIVI (dal v1
  // renderSpazioAllegati/renderRegistroModifiche/renderCestino/
  // renderPromemoriaSection). Ogni stringa è pinnata sul sorgente v1 dal
  // contract test (drift ⇒ rosso). =====

  // Spazio allegati (v1 renderSpazioAllegati r.3170-3205)
  spazioTotale: 'Totale occupato',
  spazioFile: 'File allegati',
  spazioTopPrefix: 'Top ',
  spazioTopSuffix: ' file più grossi',
  spazioVuoto: 'Nessun allegato caricato nel modulo Amministrazione.',
  spazioErrore: 'Errore nel calcolo dello spazio occupato: ',

  // Registro modifiche (v1 renderRegistroModifiche r.3207-3307)
  regEntita: 'Entità',
  regTutte: '— tutte —',
  regAutore: 'Autore',
  regAutorePh: 'id utente',
  regDal: 'Dal',
  regAl: 'Al',
  regVuoto: 'Nessuna modifica trovata con questi filtri.',
  regMostraAltri: 'Mostra altri',
  regMostratePrefix: 'Mostrate ',
  regMostrateSep: ' di ',
  regErrore: 'Errore nel caricamento del registro: ',

  // Cestino (v1 renderCestino r.3309-3375)
  cestVuoto: 'Il cestino è vuoto.',
  cestRipristina: 'Ripristina',
  cestRipristinato: 'Ripristinato',
  cestRipristinoKo: 'Ripristino NON riuscito: ',
  cestDoppionePrefix: 'doppione unito in ',
  cestErrore: 'Errore nel caricamento del cestino: ',
  cestElemento: ' element',
  cestNelCestino: ' nel cestino.',

  // Promemoria email (v1 renderPromemoriaSection r.3377-3500)
  promAttivi: 'Promemoria attivi',
  promAttiviDesc: 'Invia una email quando una scadenza entra nella finestra di preavviso',
  promEmail: 'Email destinatario',
  promEmailPh: 'amministrazione@azienda.it',
  promSalva: 'Salva',
  promTest: 'Invia email di prova',
  promAnticipo: 'Anticipo default (giorni)',
  promAnticipoPh: 'es. 30',
  promAnticipoWarn: 'conferma col commercialista',
  promPausaDal: 'Pausa invii dal',
  promPausaAl: 'Pausa invii al',
  promPausaHint: 'La pausa non si ripete: va reimpostata ogni anno. Lascia vuoto per nessuna pausa.',
  promUltimiInvii: 'Ultimi invii',
  promNessunInvio: 'Nessun invio registrato.',
  promPausaIncompleta: 'Compila entrambe le date della pausa, oppure lasciale entrambe vuote.',
  promDatiNonSalvati: 'Dati NON salvati: ',
  promInviataA: 'Inviata a ',
  promNonInviata: 'NON inviata: ',
  promSalvato: 'Promemoria salvato',
  promBadgeOk: 'ok',
  promBadgeErrore: 'errore',
};

// menu a tendina VERBATIM (v1 r.3046/3048)
export const FORMA_GIURIDICA = ['srl', 'spa', 'snc', 'sas', 'ditta'];
export const REGIME_IVA = ['trimestrale', 'mensile'];

// 8 flag "Caratteristiche fiscali" — VERBATIM v1 r.3022-3031 (chiave, label,
// descrizione). L'ordine è quello del v1 (pinnato dal contract test).
export const BOOLS = [
  ['ha_dipendenti', 'Ha dipendenti', 'Attiva F24 ritenute, UniEmens, CU, 770, INAIL'],
  ['sostituto_imposta', "Sostituto d'imposta", 'Attiva CU e 770'],
  ['soggetto_irap', 'Soggetto IRAP', 'Attiva IRAP nei versamenti redditi'],
  ['possiede_immobili', 'Possiede immobili', 'Attiva IMU acconto e saldo'],
  ['operazioni_estero_ue', 'Operazioni estero UE', 'Attiva INTRASTAT ed esterometro'],
  ['edilizia_reverse_charge', 'Reverse charge edile', 'Promemoria split payment / reverse charge edilizia'],
  ['gestione_rifiuti', 'Gestione rifiuti / MUD', 'Attiva MUD, registri rifiuti, CONAI'],
  ['settore_edile', 'Settore edile', 'Attiva Cassa Edile, congruità, patente a crediti'],
];

// data breve italiana (v1 dd r.3596/3683): 'gg/mm/aaaa' o '—'
export function dd(s) {
  return s ? new Date(s + 'T00:00:00').toLocaleDateString('it-IT') : PA_TEXTS.dash;
}

// stato locale iniziale dei flag (v1 r.3098: !!p[chiave])
export function boolStateIniziale(p) {
  const out = {};
  BOOLS.forEach((b) => { out[b[0]] = !!(p && p[b[0]]); });
  return out;
}

// payload PATCH /profilo — PARITÀ BYTE v1 r.3134-3145 (Number|null, fallback
// 12/31 su mese/giorno, flag 1/0). form = {ragione_sociale, partita_iva,
// codice_fiscale, codice_ateco, forma_giuridica, regime_iva,
// mese_chiusura_esercizio, giorno_chiusura_esercizio, pat_principale} (stringhe
// grezze dai campi); boolState = {chiave:boolean}.
export function buildProfiloPayload(form, boolState) {
  const payload = {
    ragione_sociale: form.ragione_sociale || null,
    partita_iva: form.partita_iva || null,
    codice_fiscale: form.codice_fiscale || null,
    codice_ateco: form.codice_ateco || null,
    forma_giuridica: form.forma_giuridica,
    regime_iva: form.regime_iva,
    mese_chiusura_esercizio: Number(form.mese_chiusura_esercizio) || 12,
    giorno_chiusura_esercizio: Number(form.giorno_chiusura_esercizio) || 31,
    pat_principale: form.pat_principale || null,
  };
  BOOLS.forEach((b) => { payload[b[0]] = boolState[b[0]] ? 1 : 0; });
  return payload;
}

// flag correnti per l'anteprima (v1 flagsCorrenti r.3109-3113): solo i booleani
// {chiave: 1|0} — la scheda anagrafica NON influenza l'applicabilità.
export function flagsCorrenti(boolState) {
  const out = {};
  BOOLS.forEach((b) => { out[b[0]] = boolState[b[0]] ? 1 : 0; });
  return out;
}

// toast dopo il salvataggio (v1 r.3149): "Profilo salvato · C nuove, N non dovute"
export function saveToast(rigenerazione) {
  const rg = rigenerazione || {};
  return 'Profilo salvato · ' + (rg.creati || 0) + ' nuove, ' + (rg.non_dovuti || 0) + ' non dovute';
}

// suffisso "nuove" dell'anteprima (v1 r.3125). QUIRK REPLICATO: per nuove>1 il
// v1 produce " nuovae scadenze …" (base ' nuova' + 'e') — parità byte, il typo
// è segnalato nel report, NON corretto in silenzio.
export function anteprimaNuoveSuffix(n) {
  return ' nuova' + (n > 1 ? 'e' : '') + ' scadenz' + (n > 1 ? 'e' : 'a') + PA_TEXTS.fragTenereOcchio;
}

// suffisso "non dovute" dell'anteprima (v1 r.3126)
export function anteprimaNonDovuteSuffix(n) {
  return ' scadenz' + (n > 1 ? 'e' : 'a') + PA_TEXTS.fragCheSmette + (n > 1 ? 'ranno' : 'rà') + PA_TEXTS.fragEssereDovut + (n > 1 ? 'e' : 'a');
}

// ---- Parametri: helper di visualizzazione (v1 r.3600-3611) ----
// badge ⚠ se la nota contiene ⚠ (v1 r.3602)
export function parametroDaConfermare(note) {
  return String(note == null ? '' : note).indexOf(IC_WARN) !== -1;
}
// finestra di validità (v1 r.3606): 'gg/mm/aaaa → gg/mm/aaaa' o '… → —'
export function parametroValiditaTxt(valido_dal, valido_al) {
  return dd(valido_dal) + ' ' + ARROW + ' ' + (valido_al ? dd(valido_al) : PA_TEXTS.dash);
}
// conferma elimina parametro (v1 r.3625): «chiave» (fallback id)
export function confirmParametro(chiaveOrId) {
  return 'Eliminare il parametro «' + chiaveOrId + '»?';
}

// ---- Proroghe: helper (v1 r.3689-3699) ----
// titolo leggibile della regola dal codice (v1 titoloRegola r.3689-3692)
export function prorogaTitoloRegola(codice, regole) {
  const r = (regole || []).find((x) => x.codice === codice);
  return r ? r.titolo : codice;
}
// data originale della proroga (v1 r.3698): la data o 'tutte'
export function prorogaDataOriginaleTxt(dataOriginale) {
  return dataOriginale ? dd(dataOriginale) : PA_TEXTS.tutte;
}

// ---- LETTURE (stessa forma/richieste del v1) ----
export async function readProfilo(win) {
  return apiPathFn(win, 'profilo.get').call(null);
}
export async function readTipologie(win) {
  const list = await apiPathFn(win, 'tipologie.list').call(null);
  return list || [];
}
export async function readParametri(win) {
  const list = await apiPathFn(win, 'parametri.list').call(null);
  return list || [];
}
// Proroghe + regole (v1 renderProrogheSection r.3686-3688: proroghe.list poi
// regole.list con catch → []): le regole servono al titolo E al select del modale.
export async function readProrogheERegole(win) {
  const list = (await apiPathFn(win, 'proroghe.list').call(null)) || [];
  let regole = [];
  try { regole = (await apiPathFn(win, 'regole.list').call(null)) || []; } catch (e) { regole = []; }
  return { list, regole };
}

// ---- SCRITTURE (deleghe + azioni dirette) ----
// anteprima dry-run (v1 r.3118): POST /profilo/anteprima-impatto {flags}
export function anteprima(win, boolState) {
  return apiPathFn(win, 'profilo.anteprimaImpatto').call(null, flagsCorrenti(boolState));
}
// salva e rigenera (v1 r.3147): PATCH /profilo, ritorna {rigenerazione}
export function salvaProfilo(win, payload) {
  return apiPathFn(win, 'profilo.update').call(null, payload);
}
// modali DELEGATI (payload identico per costruzione): passano il PROPRIO onSaved
// del v3 (refresh della sola sezione) — mai renderProfilo diretto (niente interceptor)
export function apriNuovaTipologia(win, onSaved) { win.AMM.editTipologia({}, onSaved); }
export function apriEditTipologia(win, rec, onSaved) { win.AMM.editTipologia(rec || {}, onSaved); }
export function apriNuovoParametro(win, onSaved) { win.AMM.editParametro({}, onSaved); }
export function apriEditParametro(win, rec, onSaved) { win.AMM.editParametro(rec || {}, onSaved); }
// "+ Nuova proroga"/edit: le regole già caricate nella sezione (D-A6.5: il v1
// le ri-fetcha su pro-new r.3160, qui si riusa la lista appena letta — stesso
// modale v1, stesso payload di scrittura; nessun effetto sulle mutazioni).
export function apriNuovaProroga(win, regole, onSaved) { win.AMM.editProroga({}, regole || [], onSaved); }
export function apriEditProroga(win, rec, regole, onSaved) { win.AMM.editProroga(rec || {}, regole || [], onSaved); }
// elimina (delSafe v1: alert d'errore se fallisce), poi onOk (toast + refresh v3)
export function eliminaParametro(win, id, onOk) {
  return win.AMM.delSafe(apiPathFn(win, 'parametri.remove').call(null, id), onOk);
}
export function eliminaProroga(win, id, onOk) {
  return win.AMM.delSafe(apiPathFn(win, 'proroghe.remove').call(null, id), onOk);
}

// =====================================================================
// A7 (ondata 37) — PARTE PURA dei 4 pannelli NATIVI (Spazio allegati /
// Registro modifiche / Cestino / Promemoria email). Formule e testi
// replicati VERBATIM dal v1 (admin-module.js), pinnati dal contract test.
// Le SCRITTURE del Promemoria delegano alla primitiva v1
// upsertParametroVigente; il Ripristino del Cestino chiama lo STESSO
// endpoint del v1 (POST /cestino/:entita/:id/ripristina, NON distruttivo —
// nel v1 non esiste alcuno "svuota"/hard-delete del cestino).
// =====================================================================

// ---- formattatori data VERBATIM dal v1 (locale it-IT) ----
// data+ora (v1 renderRegistroModifiche/renderCestino: dt): 'gg/mm/aaaa hh:mm'
// o '—'; stringa non-data ⇒ restituita così com'è (nel v1 esc(s), qui il JSX
// fa l'escaping).
export function fmtDataOra(s) {
  if (!s) return PA_TEXTS.dash;
  const d = new Date(String(s).replace(' ', 'T'));
  if (isNaN(d.getTime())) return String(s);
  return d.toLocaleDateString('it-IT') + ' ' + d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
}
// data sola (v1 renderSpazioAllegati: dt): 'gg/mm/aaaa' o '—'
export function fmtDataSola(s) {
  if (!s) return PA_TEXTS.dash;
  const d = new Date(String(s).replace(' ', 'T'));
  if (isNaN(d.getTime())) return String(s);
  return d.toLocaleDateString('it-IT');
}
// timestamp del log Promemoria (v1 renderPromemoriaSection: dt) — replicato
// col suo quirk (il ramo ternario aggiunge sempre '', resta toLocaleString).
export function fmtPromemoriaLog(s) {
  return s ? new Date(s.replace(' ', 'T') + (s.indexOf('Z') === -1 && s.indexOf('T') === -1 ? '' : '')).toLocaleString('it-IT') : PA_TEXTS.dash;
}

// ---- Spazio allegati ----
// dimensione leggibile (v1: human): B / KB / MB
export function humanBytes(n) {
  if (n == null) return '';
  if (n < 1024) return n + ' B';
  if (n < 1048576) return Math.round(n / 1024) + ' KB';
  return (n / 1048576).toFixed(1) + ' MB';
}
export function readSpazio(win) {
  return apiPathFn(win, 'attachments.spazioOccupato').call(null);
}

// ---- Registro modifiche ----
// whitelist entità→label (v1 STORIA_ENTITA_LABEL r.3215-3225, VERBATIM —
// sincronia col backend STORIA_ENTITA di amm.routes.js, come nel v1).
export const STORIA_ENTITA_LABEL = {
  amm_scadenze: 'Scadenza', amm_f24: 'F24 (testata)', amm_f24_righe: 'F24 (riga)',
  amm_rate: 'Rata', amm_regole_fiscali: 'Regola', amm_dipendenti: 'Dipendente',
  amm_veicoli: 'Veicolo', amm_polizze: 'Polizza', amm_atti: 'Atto', amm_proroghe: 'Proroga',
  amm_parametri: 'Parametro', amm_profilo_azienda: 'Profilo azienda', amm_tributi_piano: 'Piano imposte',
  amm_dipendente_adempimenti: 'Adempimento dipendente', amm_esercizio_documenti: 'Documento esercizio',
  amm_catalogo_proposte: 'Proposta AI', amm_dipendente_trattenute: 'Trattenuta dipendente',
  amm_busta_voci: 'Voce busta paga', amm_dipendente_eventi: 'Evento dipendente',
  amm_crediti: 'Credito d\'imposta', amm_immobili: 'Immobile',
  amm_cantieri: 'Cantiere',
};
// colori del badge origine (v1 storiaOrigineBadge r.4695): [bg, fg]. default ui.
export const ORIGINE_COLORI = { ui: ['#f1f5f9', '#475569'], motore: ['#cffafe', '#0e7490'], import: ['#fef3c7', '#92400e'], ai: ['#ede9fe', '#5b21b6'] };
export function origineBadge(origine) {
  const c = ORIGINE_COLORI[origine] || ORIGINE_COLORI.ui;
  return { bg: c[0], fg: c[1], label: origine || 'ui' };
}
// testo del contatore (v1 r.3271): 'Mostrate X di Y' oppure '' se totale falsy.
export function registroContaTesto(mostrate, totale) {
  return totale ? (PA_TEXTS.regMostratePrefix + mostrate + PA_TEXTS.regMostrateSep + totale) : '';
}
export const REG_LIMIT = 25; // v1 state.limit
// lettura paginata (v1 window.api.amm.storiaRegistro): ripulisce i filtri vuoti
// come fa l'API v1 (undefined/null/'' scartati) delegando alla stessa funzione.
export function readStoriaRegistro(win, filtri) {
  return apiPathFn(win, 'storiaRegistro').call(null, filtri);
}

// ---- Cestino ----
// whitelist entità→label (v1 CEST_ENTITA_LABEL r.3314-3317, VERBATIM).
export const CEST_ENTITA_LABEL = {
  scadenze: 'Scadenza', f24: 'F24', documenti: 'Documento', veicoli: 'Veicolo',
  polizze: 'Polizza', atti: 'Atto', dipendenti: 'Dipendente', 'esercizi-documenti': 'Documento esercizio',
};
// contatore cestino (v1 r.3359): 'N element(o|i) nel cestino.'
export function cestinoContaTesto(n) {
  return n + PA_TEXTS.cestElemento + (n === 1 ? 'o' : 'i') + PA_TEXTS.cestNelCestino;
}
export function readCestino(win) {
  return apiPathFn(win, 'cestino.list').call(null);
}
// RIPRISTINO (NON distruttivo, stesso endpoint del v1): POST /cestino/:e/:id/ripristina
export function ripristinaCestino(win, entita, id) {
  return apiPathFn(win, 'cestino.ripristina').call(null, entita, id);
}

// ---- Promemoria email ----
export function readPromemoriaStato(win) {
  return apiPathFn(win, 'promemoria.stato').call(null);
}
// valore VIGENTE OGGI di una chiave in una lista amm_parametri (v1
// vigenteDiParametro r.3505-3509): più recente valido_dal; null se assente.
export function vigenteDiParametro(lista, chiave) {
  const vigente = (lista || []).filter((p) => p.chiave === chiave)
    .sort((a, b) => (b.valido_dal || '').localeCompare(a.valido_dal || ''))[0];
  return vigente ? vigente.valore : null;
}
// guardia client pausa (v1 r.3466): un solo estremo valorizzato ⇒ incompleta.
export function pausaIncompleta(dal, al) {
  return (!!dal && !al) || (!dal && !!al);
}
// messaggio d'errore del salva (v1 r.3481): dettagli[0].msg oppure message.
export function promemoriaSalvaErrMsg(e) {
  return (e && e.dettagli && e.dettagli[0] && e.dettagli[0].msg) || (e && e.message) || String(e);
}
// SALVA (v1 r.3471-3475): 5 upsert IN ORDINE della riga vigente, DELEGATI alla
// primitiva v1 upsertParametroVigente (nessuna scrittura reimplementata). Stesse
// chiavi/valori/fallback ('' quando vuoto) del v1.
export function salvaPromemoria(win, { email, attivo, anticipo, pausaDal, pausaAl }) {
  const up = (chiave, valore) => win.AMM.upsertParametroVigente(chiave, valore);
  return up('promemoria_email', email || '')
    .then(() => up('promemoria_attivo', attivo ? '1' : '0'))
    .then(() => up('promemoria_anticipo_default', anticipo || ''))
    .then(() => up('promemoria_pausa_dal', pausaDal || ''))
    .then(() => up('promemoria_pausa_al', pausaAl || ''));
}
// invio di prova (v1 r.3492): POST /promemoria/test {email?} (undefined ⇒ usa
// il destinatario salvato lato server).
export function inviaProvaPromemoria(win, email) {
  return apiPathFn(win, 'promemoria.test').call(null, email || undefined);
}
